<template>
  <div class="parking-spaces-management">
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12>
          <h1 class="display-1 font-weight-bold mb-2">Gestión de Espacios de Estacionamiento</h1>
          <p class="subtitle grey--text">Crear y gestionar espacios de estacionamiento del edificio corporativo</p>
        </v-flex>
      </v-layout>

      <!-- Botón para crear nuevo espacio -->
      <v-layout row wrap class="mb-4">
        <v-flex xs12 sm6>
          <v-btn color="primary" large @click="openNewSpaceDialog">
            <v-icon left>add</v-icon>
            Nuevo Espacio
          </v-btn>
        </v-flex>
        <v-flex xs12 sm6>
          <v-text-field
            v-model="searchSpaces"
            prepend-icon="search"
            label="Buscar espacios..."
            outlined
            dense
          ></v-text-field>
        </v-flex>
      </v-layout>

      <!-- Error general -->
      <v-layout row wrap v-if="generalError" class="mb-3">
        <v-flex xs12>
          <v-alert type="error" dismissible :value="true" @input="generalError = ''">
            {{ generalError }}
          </v-alert>
        </v-flex>
      </v-layout>

      <!-- Tabla de espacios -->
      <v-layout row wrap>
        <v-flex xs12>
          <v-card class="elevation-2">
            <v-data-table
              :headers="headers"
              :items="filteredSpaces"
              :loading="loadingSpaces"
              class="elevation-1"
              :no-data-text="'No hay espacios de estacionamiento registrados'"
              :rows-per-page-items="[10, 20, 50]"
              :rows-per-page="10"
            >
              <template v-slot:items="props">
                <td class="text-xs-center">{{ props.item.code }}</td>
                <td class="text-xs-center">{{ props.item.type }}</td>
                <td class="text-xs-center">
                  <v-chip :color="getStatusColor(props.item.status)" text-color="white" small>
                    {{ props.item.status }}
                  </v-chip>
                </td>
                <td class="text-xs-center">{{ props.item.company_name || '-' }}</td>
                <td class="text-xs-center">
                  <v-icon small class="mr-2" @click="editSpace(props.item)">edit</v-icon>
                  <v-icon small class="mr-2" @click="blockSpace(props.item)">block</v-icon>
                  <v-icon small color="red" @click="deleteSpace(props.item)">delete</v-icon>
                </td>
              </template>
              <template v-slot:no-results>
                <v-alert type="info" :value="true">No se encontraron espacios</v-alert>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Diálogo para crear/editar espacio -->
    <v-dialog v-model="dialogSpace" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ editingSpace ? 'Editar Espacio' : 'Nuevo Espacio de Estacionamiento' }}</span>
        </v-card-title>

        <v-card-text>
          <v-form ref="formSpace" v-model="validSpace">
            <v-text-field
              v-model="form.code"
              label="Código del espacio"
              prepend-icon="local_parking"
              placeholder="Ej: A-01, B-05"
              :rules="[v => !!v || 'El código es requerido']"
              :error-messages="errores.code"
              outlined
              dense
              class="mb-3"
            ></v-text-field>

            <v-select
              v-model="form.type"
              :items="spaceTypes"
              label="Tipo de espacio"
              prepend-icon="directions_car"
              :rules="[v => !!v || 'Selecciona un tipo']"
              :error-messages="errores.type"
              outlined
              dense
              class="mb-3"
            ></v-select>

            <v-select
              v-model="form.status"
              :items="statusOptions"
              label="Estado"
              prepend-icon="info"
              :rules="[v => !!v || 'Selecciona un estado']"
              :error-messages="errores.status"
              outlined
              dense
            ></v-select>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" flat @click="closeDialog">Cancelar</v-btn>
          <v-btn color="primary" :loading="savingSpace" @click="saveSpace">
            {{ editingSpace ? 'Actualizar' : 'Crear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo de confirmación de bloqueo -->
    <v-dialog v-model="dialogBlock" max-width="400px">
      <v-card>
        <v-card-title>Bloquear Espacio</v-card-title>
        <v-card-text>
          <p>¿Deseas bloquear el espacio <strong>{{ selectedSpace ? selectedSpace.code : '' }}</strong>?</p>
          <v-text-field
            v-model="blockReason"
            label="Motivo del bloqueo"
            prepend-icon="note"
            outlined
            dense
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" flat @click="dialogBlock = false">Cancelar</v-btn>
          <v-btn color="warning" :loading="savingSpace" @click="confirmBlock">Bloquear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo de confirmación de eliminación -->
    <v-dialog v-model="dialogDelete" max-width="400px">
      <v-card>
        <v-card-title>Eliminar Espacio</v-card-title>
        <v-card-text>
          ¿Estás seguro de que deseas eliminar el espacio <strong>{{ selectedSpace ? selectedSpace.code : '' }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" flat @click="dialogDelete = false">Cancelar</v-btn>
          <v-btn color="error" :loading="savingSpace" @click="confirmDelete">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import '@/plugins/axios'
import axios from 'axios'
import store from '@/store'

const API_BASE_URL = 'https://empresa-service.intrare.app'

export default {
  name: 'ParkingSpacesManagement',
  data() {
    return {
      loadingSpaces: false,
      savingSpace: false,
      searchSpaces: '',
      dialogSpace: false,
      dialogBlock: false,
      dialogDelete: false,
      validSpace: true,
      editingSpace: null,
      selectedSpace: null,
      blockReason: '',
      errores: {},
      generalError: '',

      form: {
        code: '',
        type: '',
        status: 'disponible'
      },

      headers: [
        { text: 'Código', value: 'code', align: 'center', width: '120px' },
        { text: 'Tipo', value: 'type', align: 'center', width: '120px' },
        { text: 'Estado', value: 'status', align: 'center', width: '150px' },
        { text: 'Empresa', value: 'company_name', align: 'center', width: '150px' },
        { text: 'Acciones', value: 'actions', align: 'center', sortable: false, width: '150px' }
      ],

      spaceTypes: [
        'Automóvil',
        'Motocicleta',
        'Bicicleta',
        'Minusválido'
      ],

      statusOptions: [
        'disponible',
        'ocupado',
        'bloqueado',
        'mantenimiento'
      ],

      spaces: []
    }
  },

  computed: {
    filteredSpaces() {
      if (!this.searchSpaces) return this.spaces
      return this.spaces.filter(space =>
        space.code.toLowerCase().includes(this.searchSpaces.toLowerCase()) ||
        space.type.toLowerCase().includes(this.searchSpaces.toLowerCase()) ||
        (space.company_name && space.company_name.toLowerCase().includes(this.searchSpaces.toLowerCase()))
      )
    }
  },

  mounted() {
    this.fetchSpaces()
  },

  methods: {
    fetchSpaces() {
      this.loadingSpaces = true
      const config = {
        method: 'get',
        url: `${API_BASE_URL}/Parking/building-spaces/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      axios(config)
        .then(response => {
          this.spaces = response.data
        })
        .catch(error => {
          this.generalError = (error.response && error.response.data && error.response.data.error) || 'Error al cargar los espacios de estacionamiento'
        })
        .finally(() => {
          this.loadingSpaces = false
        })
    },

    openNewSpaceDialog() {
      this.editingSpace = null
      this.errores = {}
      this.form = { code: '', type: '', status: 'disponible' }
      this.dialogSpace = true
    },

    editSpace(space) {
      this.editingSpace = space
      this.errores = {}
      this.form = { code: space.code, type: space.type, status: space.status }
      this.dialogSpace = true
    },

    closeDialog() {
      this.dialogSpace = false
      this.editingSpace = null
      this.errores = {}
      this.form = { code: '', type: '', status: 'disponible' }
    },

    saveSpace() {
      if (!this.$refs.formSpace.validate()) return

      this.errores = {}
      this.savingSpace = true

      const config = {
        method: this.editingSpace ? 'patch' : 'post',
        url: this.editingSpace
          ? `${API_BASE_URL}/Parking/building-spaces/${this.editingSpace.id}/`
          : `${API_BASE_URL}/Parking/building-spaces/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        data: {
          code: this.form.code,
          type: this.form.type,
          status: this.form.status
        }
      }

      axios(config)
        .then(() => {
          this.$store.commit('snackbar_change_message', 'Espacio guardado exitosamente')
          this.$store.commit('snackbar_activate_true')
          this.closeDialog()
          this.fetchSpaces()
        })
        .catch(error => {
          if (error.response && error.response.status === 400 && typeof error.response.data === 'object') {
            this.errores = error.response.data
          } else {
            this.generalError = (error.response && error.response.data && error.response.data.error) || 'Error al guardar el espacio'
          }
        })
        .finally(() => {
          this.savingSpace = false
        })
    },

    blockSpace(space) {
      this.selectedSpace = space
      this.blockReason = ''
      this.dialogBlock = true
    },

    confirmBlock() {
      this.savingSpace = true
      const config = {
        method: 'patch',
        url: `${API_BASE_URL}/Parking/building-spaces/${this.selectedSpace.id}/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        data: {
          status: 'bloqueado',
          block_reason: this.blockReason
        }
      }
      axios(config)
        .then(() => {
          this.$store.commit('snackbar_change_message', 'Espacio bloqueado')
          this.$store.commit('snackbar_activate_true')
          this.dialogBlock = false
          this.fetchSpaces()
        })
        .catch(error => {
          this.generalError = (error.response && error.response.data && error.response.data.error) || 'Error al bloquear el espacio'
        })
        .finally(() => {
          this.savingSpace = false
        })
    },

    deleteSpace(space) {
      this.selectedSpace = space
      this.dialogDelete = true
    },

    confirmDelete() {
      this.savingSpace = true
      const config = {
        method: 'delete',
        url: `${API_BASE_URL}/Parking/building-spaces/${this.selectedSpace.id}/`,
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      axios(config)
        .then(() => {
          this.$store.commit('snackbar_change_message', 'Espacio eliminado')
          this.$store.commit('snackbar_activate_true')
          this.dialogDelete = false
          this.fetchSpaces()
        })
        .catch(error => {
          this.dialogDelete = false
          this.generalError = (error.response && error.response.data && error.response.data.error) || 'Error al eliminar el espacio'
        })
        .finally(() => {
          this.savingSpace = false
        })
    },

    getStatusColor(status) {
      const colors = {
        disponible: 'success',
        ocupado: 'warning',
        bloqueado: 'error',
        mantenimiento: 'info'
      }
      return colors[status] || 'grey'
    }
  }
}
</script>

<style scoped>
.parking-spaces-management {
  padding: 20px;
}
</style>
